import { Injectable, Inject, DOCUMENT } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';


@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  constructor(
    private auth: Auth,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.initializeThemeSetting();
  }

  private updateUserThemePreference(theme: 'light' | 'dark' | 'system') {
    localStorage.setItem('user-theme', theme);
  }

  private applyUserThemePreference() {
    const theme = (localStorage.getItem('user-theme') || 'system') as
      | 'light'
      | 'dark'
      | 'system';
    this.setTheme(theme);
  }

  public initializeThemeSetting() {
    this.applyUserThemePreference();
    this.observeSystemTheme();
    authState(this.auth).subscribe(() => {
      this.applyUserThemePreference();
    });
  }

  private observeSystemTheme() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      if (localStorage.getItem('user-theme') === 'system') {
        this.applyDarkMode(mediaQuery.matches);
      }
    });
    if (localStorage.getItem('user-theme') === 'system') {
      this.applyDarkMode(mediaQuery.matches);
    }
  }

  private applyDarkMode(isDark: boolean) {
    this.updateMetaThemeColor(isDark ? '#000000' : '#ffffff');
    if (isDark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }

  setTheme(theme: 'light' | 'dark' | 'system') {
    switch (theme) {
      case 'light':
        document.body.classList.remove('dark');
        this.updateMetaThemeColor('#ffffff');
        break;
      case 'dark':
        document.body.classList.add('dark');
        this.updateMetaThemeColor('#000000');
        break;
      case 'system':
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)'
        ).matches;
        this.applyDarkMode(prefersDark);
        break;
    }
    this.updateUserThemePreference(theme);
  }

  getCurrentTheme(): 'light' | 'dark' | 'system' {
    return (
      (localStorage.getItem('user-theme') as 'light' | 'dark' | 'system') ||
      'system'
    );
  }

  getOs(): 'ios' | 'md' {
    if (document.documentElement.classList.contains('ios')) return 'ios';
    return 'md';
  }

  getTheme(): 'light' | 'dark' {
    if (document.body.classList.contains('dark')) return 'dark';
    return 'light';
  }

  private updateMetaThemeColor(color: string) {
    const themeColorMetaTag = this.document.querySelector(
      'meta[name=theme-color]'
    );
    if (themeColorMetaTag) {
      themeColorMetaTag.setAttribute('content', color);
    }
  }
}
